<script lang="ts">
  import { viewport } from '../lib/stores/viewport';
  import Icon from './Icon.svelte';
  import type { CalendarEvent } from '../lib/types';
  import {
    eventTypeColors,
    eventTypeIcons,
    getWeekStart,
    eventsForDay,
    eventsForWeek,
    eventsForMonth,
    parseCalendarDate,
    getTodayStr,
    formatCalendarDate,
    formatEventTimeShort,
    formatEventDuration,
  } from '../lib/utils';
  import {
    isEventsRailOpen,
    eventsRailData,
    closeEventsRail,
    openEventModal,
    openEventModalForEdit,
  } from '../lib/stores/ui';

  interface Props {
    events: CalendarEvent[];
  }

  let { events }: Props = $props();

  // Derive from store
  let isOpen = $derived($isEventsRailOpen);
  let data = $derived($eventsRailData);

  // Filter events based on period type
  let filteredEvents = $derived.by(() => {
    if (!data) return [];

    const periodType = data.periodType || 'month';
    const periodKey = data.periodKey || data.monthKey || '';

    switch (periodType) {
      case 'day':
        return eventsForDay(events, periodKey);
      case 'week':
        return eventsForWeek(events, periodKey);
      case 'month':
      default:
        return eventsForMonth(events, periodKey);
    }
  });

  // Group events by date
  let eventsByDate = $derived.by(() => {
    const groups: Record<string, CalendarEvent[]> = {};
    for (const event of filteredEvents) {
      const date = event.date;
      if (!groups[date]) groups[date] = [];
      groups[date].push(event);
    }
    // Sort by date
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  });

  // Get title based on period type
  let railTitle = $derived.by(() => {
    if (!data) return 'Events';
    const periodType = data.periodType || 'month';
    switch (periodType) {
      case 'day':
        return `${data.periodLabel}`;
      case 'week':
        return `Week ${data.periodLabel}`;
      case 'month':
      default:
        return `${data.periodLabel || data.monthLabel} Events`;
    }
  });

  // Get default date for adding new event
  let defaultAddDate = $derived.by(() => {
    if (!data) return getTodayStr();
    const periodType = data.periodType || 'month';
    const periodKey = data.periodKey || data.monthKey || '';

    switch (periodType) {
      case 'day':
        return periodKey;
      case 'week': {
        const weekStart = getWeekStart(periodKey);
        return weekStart.toISOString().split('T')[0];
      }
      case 'month':
      default:
        return `${periodKey}-01`;
    }
  });

  function formatDate(dateStr: string): string {
    const date = parseCalendarDate(dateStr);
    const day = date.getUTCDate();
    const weekday = formatCalendarDate(date, { weekday: 'short' });
    return `${weekday} ${day}`;
  }
</script>

{#if isOpen && data}
  <aside class="events-rail" class:in-sheet={$viewport.isMobile} data-sheet-fill>
    <!-- Header -->
    <header class="rail-header" data-sheet-drag-handle>
      <div class="header-title">
        <span class="header-icon">📅</span>
        <h2 data-sheet-title>{railTitle}</h2>
        <span class="header-count">{filteredEvents.length}</span>
      </div>
      <button
        type="button"
        class="close-btn"
        onclick={closeEventsRail}
        title="Close panel"
        data-sheet-close
      >
        {#if $viewport.isMobile}<Icon name="close" />{:else}✕{/if}
      </button>
    </header>

    <!-- Scrollable content -->
    <div class="rail-content">
      {#if eventsByDate.length > 0}
        {#each eventsByDate as [date, dateEvents]}
          <div class="date-group">
            {#if data.periodType !== 'day'}
              <div class="date-header">
                <span class="date-day">{formatDate(date)}</span>
              </div>
            {/if}
            <div class="event-list">
              {#each dateEvents as event (event.id)}
                {@const timeStr = formatEventTimeShort(event)}
                {@const duration = formatEventDuration(event)}
                <button
                  type="button"
                  class="event-card {eventTypeColors[event.type]}"
                  onclick={() => openEventModalForEdit(event)}
                  title={timeStr
                    ? `${timeStr} · ${event.title}${duration ? ` (${duration})` : ''}`
                    : event.title}
                >
                  <span class="event-icon">{eventTypeIcons[event.type]}</span>
                  {#if timeStr}
                    <span class="event-time">{timeStr}</span>
                  {/if}
                  <span class="event-title">{event.title}</span>
                  {#if duration}
                    <span class="event-duration">({duration})</span>
                  {/if}
                </button>
              {/each}
            </div>
          </div>
        {/each}
      {:else}
        <div class="empty-state">
          <span class="empty-icon">📭</span>
          <span class="empty-text">No events</span>
          <button
            type="button"
            class="add-event-btn"
            onclick={() => openEventModal(defaultAddDate)}
          >
            + Add event
          </button>
        </div>
      {/if}
    </div>

    <!-- Footer -->
    <footer class="rail-footer">
      <button type="button" class="add-event-link" onclick={() => openEventModal(defaultAddDate)}>
        + Add event
      </button>
    </footer>
  </aside>
{/if}

<style>
  .events-rail {
    width: 14rem;
    background: #f9fafb;
    border-left: 1px solid #e5e7eb;
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
  }

  .rail-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.5rem;
    border-bottom: 1px solid #e5e7eb;
    background: white;
  }

  .header-title {
    display: flex;
    align-items: center;
    gap: 0.375rem;
  }

  .header-icon {
    font-size: 0.875rem;
  }

  .header-title h2 {
    font-size: 0.75rem;
    font-weight: 700;
    color: #374151;
  }

  .header-count {
    font-size: 0.625rem;
    color: #6b7280;
    font-weight: 500;
    background: #f3f4f6;
    padding: 0.125rem 0.375rem;
    border-radius: 9999px;
  }

  .close-btn {
    width: 1.5rem;
    height: 1.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    color: #9ca3af;
    background: none;
    border: none;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: all 0.15s;
  }

  .close-btn:hover {
    color: #374151;
    background: #f3f4f6;
  }

  .rail-content {
    flex: 1;
    overflow-y: auto;
    padding: 0.375rem;
  }

  .date-group {
    margin-bottom: 0.5rem;
  }

  .date-group:last-child {
    margin-bottom: 0;
  }

  .date-header {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.25rem 0.375rem;
    background: #f3f4f6;
    border-radius: 0.25rem;
    margin-bottom: 0.25rem;
  }

  .date-day {
    font-size: 0.6875rem;
    font-weight: 700;
    color: #374151;
  }

  .event-list {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    padding-left: 0.375rem;
  }

  .event-card {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.25rem 0.375rem;
    border-radius: 0.25rem;
    font-size: 0.625rem;
    cursor: pointer;
    text-align: left;
    transition: all 0.15s;
    border: none;
  }

  .event-card:hover {
    opacity: 0.85;
  }

  .event-icon {
    font-size: 0.5625rem;
    flex-shrink: 0;
  }

  .event-time {
    font-size: 0.5625rem;
    font-weight: 600;
    opacity: 0.7;
    flex-shrink: 0;
  }

  .event-title {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
    flex: 1;
  }

  .event-duration {
    font-size: 0.5rem;
    opacity: 0.7;
    flex-shrink: 0;
  }

  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    color: #9ca3af;
    gap: 0.375rem;
    padding: 2rem;
  }

  .empty-icon {
    font-size: 1.5rem;
  }

  .empty-text {
    font-size: 0.625rem;
  }

  .add-event-btn {
    margin-top: 0.5rem;
    padding: 0.25rem 0.5rem;
    font-size: 0.625rem;
    color: #3b82f6;
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: all 0.15s;
  }

  .add-event-btn:hover {
    background: #dbeafe;
  }

  .rail-footer {
    padding: 0.375rem 0.5rem;
    border-top: 1px solid #e5e7eb;
    background: white;
    font-size: 0.5625rem;
    color: #9ca3af;
    text-align: center;
  }

  .add-event-link {
    font-size: 0.625rem;
    color: #3b82f6;
    background: none;
    border: none;
    cursor: pointer;
    transition: color 0.15s;
  }

  .add-event-link:hover {
    color: #1d4ed8;
  }

  /* Mobile: inside the Dashboard's rail bottom sheet. */
  .events-rail.in-sheet {
    flex: 1 1 auto;
    min-height: 0;
    border-left: none;
  }

  .events-rail.in-sheet .rail-content {
    min-height: 0;
    overscroll-behavior: contain;
  }

  .events-rail.in-sheet .header-icon {
    font-size: 1.0625rem;
  }
</style>
